"use client"

import { PriceWrapper } from "@/app/price"
import { Trans } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { ChangeTrend } from "../leaderboard/view/change-trend"
import { useCollectionStatisticReatom } from "../store/use-collection.reatom"
import { Count } from "../view/count"
import { TrendDisplay } from "./trend-display"

function StatisticItem({
  title,
  content,
}: {
  title: React.ReactNode
  content: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-end gap-y-1">
      <span className="text-nowrap text-foreground/60 text-xs md:text-wrap">
        {title}
      </span>
      <h4 className="text-nowrap text-lg md:text-wrap">{content}</h4>
    </div>
  )
}

export function StatisticReatom() {
  return <StatisticContentReatom />
}

const StatisticContentReatom = reatomComponent(() => {
  const statistic = useCollectionStatisticReatom()

  // Show skeleton while loading (client-side fetch)
  if (!statistic) {
    return <StatisticSkeletonReatom />
  }

  return (
    <div
      className="flex items-end gap-x-4 gap-y-2 overflow-x-auto md:flex-wrap md:items-center md:gap-x-6 md:overflow-visible"
      data-testid="collection-statistic"
    >
      <StatisticItem
        title={<Trans>Floor Price</Trans>}
        content={
          statistic.floor || statistic.dayFloorChange ? (
            <div className="flex items-center gap-x-1">
              {statistic.floor ? (
                <PriceWrapper {...statistic.floor} />
              ) : (
                <UndefinedLabel />
              )}
              {/* No change to show — e.g. a Magic Eden floor, which comes with
                  no 24h history — is left out, as in the sticky header. */}
              {statistic.dayFloorChange ? (
                <ChangeTrend
                  className="gap-0 text-xs"
                  changePercent={statistic.dayFloorChange}
                  TrendDisplay={TrendDisplay}
                />
              ) : null}
            </div>
          ) : (
            <UndefinedLabel />
          )
        }
      />
      <StatisticItem
        title={<Trans>Top Offer</Trans>}
        content={
          statistic.topOffer ? (
            <PriceWrapper {...statistic.topOffer} />
          ) : (
            <UndefinedLabel />
          )
        }
      />
      <StatisticItem
        title={<Trans>24h Vol</Trans>}
        content={
          statistic.dayVolume ? (
            <PriceWrapper {...statistic.dayVolume} />
          ) : (
            <UndefinedLabel />
          )
        }
      />
      {/* Magic Eden publishes no all-time volume, so a Solana collection
          other marketplaces trade shows 7 days counted on all of them. */}
      {statistic.showWeekVolume ? (
        <StatisticItem
          title={<Trans>7d Vol</Trans>}
          content={
            statistic.weekVolume ? (
              <PriceWrapper {...statistic.weekVolume} />
            ) : (
              <UndefinedLabel />
            )
          }
        />
      ) : (
        <StatisticItem
          title={<Trans>All Vol</Trans>}
          content={
            statistic.totalVolume ? (
              <PriceWrapper {...statistic.totalVolume} />
            ) : (
              <UndefinedLabel />
            )
          }
        />
      )}
      <StatisticItem
        title={<Trans>Listed / Supply</Trans>}
        content={
          <Trans>
            <Count value={statistic.listedItems} /> /{" "}
            <Count value={statistic.items} />
          </Trans>
        }
      />
      <StatisticItem
        title={<Trans>Owners</Trans>}
        content={<Count value={statistic.owners} />}
      />
    </div>
  )
})

export function StatisticSkeletonReatom() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-x-6">
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
      <StatisticItem title={<TitleSkeleton />} content={<SubtitleSkeleton />} />
    </div>
  )
}

function TitleSkeleton() {
  return <Skeleton className="h-4 w-12" />
}

function SubtitleSkeleton() {
  return <Skeleton className="h-6 w-20" />
}

function UndefinedLabel({ className }: { className?: string }) {
  return <span className={cn("text-foreground/60", className)}>—</span>
}
